// Kill Sheet — canonical Surface Line Volume resolver.
//
// The physical piping from pump discharge to the drillstring entry
// (standpipe + rotary hose / manifold) has exactly one canonical source:
// the active Rig Engineering Session's bound Rig Data Sheet --
// rigSheet.payload.wellControlVolumes.surfaceLineVolume -- resolved via
// the RCE path "rig.surfaceLineVolBbl" (lib/rce/derivers.js), the same
// path the canonical Kill Sheet (System B) already consumes.
//
// Unlike Drill Pipe geometry (see ./drillPipeResolver.js) there is no
// legacy-profile fallback for this value -- it never existed anywhere
// before the canonical Rig Data Sheet, so there is nothing to fall back
// to. Unresolvable -> null; the cell renders blank exactly like any other
// unresolved derived field, never a fabricated number.
//
// Units: the RCE path name encodes its unit ("...Bbl") because that's how
// the Rig Data Sheet stores it, unconverted. Only IMP Kill Sheets can use
// it directly without risking a silent bbl/m3 mix-up, so this resolver
// deliberately returns nothing for an SI session rather than guess a
// conversion the source data doesn't actually support yet.
//
// Storage/resolver readers are injectable via `deps` for Node unit tests.

import { getEngineeringSession } from "@/lib/rce/session";
import { resolve } from "@/lib/rce/resolver";

const _num = (v) => {
  if (v === "" || v == null) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * resolveSurfaceLineVolumeBbl(units, deps?) →
 *   { valueBbl: number, source: "rig" } | { valueBbl: null, source: null }
 */
export function resolveSurfaceLineVolumeBbl(units, deps = {}) {
  if (units !== "IMP") return { valueBbl: null, source: null };

  const readSession  = deps.getEngineeringSession || getEngineeringSession;
  const resolveField = deps.resolve || resolve;

  let eng = null;
  try { eng = readSession(); } catch { eng = null; }
  const rigId = (eng && eng.rigId) || "";
  if (!rigId) return { valueBbl: null, source: null };

  let r = null;
  try { r = resolveField("rig.surfaceLineVolBbl", { rigId }); } catch { r = null; }
  if (!r || r.confidence === "unknown" || r.confidence === "conflict") {
    return { valueBbl: null, source: null };
  }
  const value = _num(r.value);
  return value != null && value > 0 ? { valueBbl: value, source: "rig" } : { valueBbl: null, source: null };
}
